import React, { useEffect, useRef, useState } from "react";
import {
  getDirForSsh,
  useSSHFolderList,
} from "@via/pages/data/data-sources/data-source.api.tsx";
import {
  AGENT_DATA_FOLDER_MESSAGES,
  type AgentUploadState,
  DataConnectionCredentials,
  DataSourceProvider,
  GetFileListFromSSHResponseBody,
  SshFileUploadPath,
} from "common";
import {
  browseNotices,
  EMPTY_LISTING,
  folderAddRefusal,
  isOutsideRootsError,
  skippedNotice,
  uploadTargetForDataSource,
} from "@via/pages/data/data-sources/ssh-data-source/agent-listing-notices.ts";
import { BucketObject } from "@via/pages/data/data-sources/utils/helpers.tsx";
import {
  sshFolderAddRequest,
  sshOpenObject,
  sshUploadPrefix,
} from "@via/pages/data/data-sources/ssh-data-source/ssh-browse-path.ts";
import FileBrowserWrapper from "@via/pages/data/data-sources/cloud-data-sources/common/file-browser/file-browser-wrapper.tsx";
import { useSshDataConnection } from "@via/hooks/useSshDataConnection.tsx";
import { useNotifications } from "@via/hooks/useNotifications.tsx";
import { fileUpload } from "@via/pages/data/data-sources/cloud-data-sources/common/file-upload.ts";
import Modal from "@via/components/modal.tsx";
import { FILE_UPLOAD_DISCLAIMER } from "@via/utils/constants.tsx";
import FileUploader, {
  FileUploaderHandle,
} from "@via/components/file-uploader/file-uploader.tsx";
import {
  BannerSize,
  BannerType,
  MessageBanner,
} from "@via/components/message-banner/message-banner.tsx";

export interface PathForm {
  path: string;
}

export enum AdditionalArea {
  DataSourceList = "dataSourceList",
  SaveDataSource = "saveDataSource",
  Filters = "filters",
}

interface SSHFolderBrowserProps {
  onContinue: any;
  cart?: BucketObject[];
  setCart?: (cart: BucketObject[]) => void;
  uploadModalOpen: boolean;
  setUploadModalOpen: (open: boolean) => void;
  isBookmark?: boolean;
  dataSourceId?: number;
  onAgentUploadStateChange?: (state?: AgentUploadState) => void;
}

const SSHFolderBrowser: React.FC<SSHFolderBrowserProps> = ({
  onContinue,
  cart,
  setCart,
  uploadModalOpen,
  setUploadModalOpen,
  isBookmark,
  dataSourceId,
  onAgentUploadStateChange,
}) => {
  const [progressPercentageList, setProgressPercentageList] = useState<
    { filename: string; progress: number }[]
  >([]);
  const [isLoading, setIsLoading] = useState(false);
  const [pathValue, setPathValue] = useState<string>("");
  const [cancelUploadFn, setCancelUploadFn] = useState<(() => void) | null>(
    null
  );
  const { setNotification, NOTIFICATION } = useNotifications();
  const [listError, setListError] = useState<unknown>(null);

  const { connectionCredentials, setConnectionCredentials } =
    useSshDataConnection();

  const [folderList, setFolderList] =
    useState<GetFileListFromSSHResponseBody>();

  const {
    mutateAsync: getFolderList,
    data,
    isPending: isGetFolderListLoading,
    isSuccess: isGetFolderListSuccess,
  } = useSSHFolderList();

  useEffect(() => {
    if (isGetFolderListSuccess && data) {
      setFolderList(data);
    }
  }, [isGetFolderListSuccess, data]);

  const {
    mutateAsync: getExtraFolderList,
    isPending: isGetExtraFolderListLoading,
  } = useSSHFolderList();

  useEffect(() => {
    if (connectionCredentials) {
      setListError(null);
      getFolderList({ ...connectionCredentials, dataSourceId }).catch(
        setListError
      );
    }
  }, [connectionCredentials]);

  // agent-5-5: the page header owns the Upload button, so it learns the folder's upload state from here.
  useEffect(() => {
    onAgentUploadStateChange?.(folderList?.upload?.state);
  }, [folderList]);

  const handlePathClick = (payload: PathForm) => {
    if (payload.path) {
      const requestParams = Object.assign({}, connectionCredentials);
      requestParams.prefix = undefined;
      requestParams.bucketName = payload.path;

      setConnectionCredentials({
        ...requestParams,
        prefix: undefined,
        bucketName: getDirForSsh(requestParams),
      });
    }
  };

  const handleObjectClick = (object: BucketObject) => {
    if (!connectionCredentials) return;
    setConnectionCredentials(sshOpenObject(connectionCredentials, object));
  };

  const handleExtraFolderObjects = async (item: any) => {
    if (!connectionCredentials) return EMPTY_LISTING;
    const extraFolderList = await getExtraFolderList(
      sshFolderAddRequest(connectionCredentials, item.name)
    );
    // Agent environments only: a partial folder is never added (spec 4.4).
    const refusal = folderAddRefusal(extraFolderList);
    if (refusal) {
      setNotification({
        id: "agent-folder-too-large",
        message: "Folder not added",
        details: refusal,
        variant: NOTIFICATION.ERROR,
      });
      return EMPTY_LISTING;
    }
    if (extraFolderList?.skipped) {
      setNotification({
        id: "agent-folder-skipped",
        message: "Some entries were left out",
        details: skippedNotice(extraFolderList.skipped, "folderAdd"),
        variant: NOTIFICATION.ALERT,
      });
    }
    return extraFolderList || {};
  };

  const handleFileUpload = (files: File[]) => {
    const uploadCredentials = {
      ...connectionCredentials,
    };
    uploadCredentials.bucketName = uploadCredentials.bucketName
      ? uploadCredentials.bucketName
      : "";

    const cancelFunctions = fileUpload(
      SshFileUploadPath,
      files,
      uploadCredentials as DataConnectionCredentials,
      setNotification,
      handleFileUploadSuccess,
      setIsLoading,
      setProgressPercentageList,
      dataSourceId ? uploadTargetForDataSource(dataSourceId) : undefined
    );
    setCancelUploadFn(() => cancelFunctions?.cancelAll);
  };

  const handleFileUploadSuccess = async () => {
    setProgressPercentageList([]);
    setUploadModalOpen(false);
    setCancelUploadFn(null);
    const requestParams = Object.assign({}, connectionCredentials);
    await getFolderList({ ...requestParams, dataSourceId });
  };

  const fileUploaderRef = useRef<FileUploaderHandle>(null);

  const handleModalClose = async (open: boolean) => {
    if (!open) {
      // Check with FileUploader if close should be prevented
      if (fileUploaderRef.current) {
        const canClose = await fileUploaderRef.current.checkBeforeClose();
        if (!canClose) {
          return; // Don't close - FileUploader will show confirmation
        }
      } else if (isLoading && cancelUploadFn) {
        // Fallback if ref not available
        return; // Don't close
      }
    }
    setUploadModalOpen(open);
  };

  const handleBeforeClose = async (): Promise<boolean> => {
    // Delegate to FileUploader to handle confirmation
    if (fileUploaderRef.current) {
      return await fileUploaderRef.current.checkBeforeClose();
    }
    // If no ref, check directly
    if (isLoading && cancelUploadFn) {
      return false; // Prevent closing - FileUploader will show confirmation
    }
    return true; // Allow closing
  };

  const handleConfirmTerminate = () => {
    // User confirmed termination - close modal
    setUploadModalOpen(false);
    setProgressPercentageList([]);
    setCancelUploadFn(null);
  };

  return (
    <>
      {isBookmark && isOutsideRootsError(listError) ? (
        <div className="mt-2">
          <MessageBanner
            messages={AGENT_DATA_FOLDER_MESSAGES.noLongerAllowed}
            type={BannerType.Warning}
            size={BannerSize.Small}
          />
        </div>
      ) : null}
      {browseNotices(folderList).length > 0 ? (
        <div className="mt-2">
          <MessageBanner
            messages={browseNotices(folderList)}
            type={BannerType.Info}
            size={BannerSize.Small}
          />
        </div>
      ) : null}
      <FileBrowserWrapper
        connectionCredentials={
          connectionCredentials as DataConnectionCredentials
        }
        folderList={folderList}
        dataSourceProvider={DataSourceProvider.SSH}
        onObjectClick={handleObjectClick}
        onPathChange={handlePathClick}
        getExtraFolderObjects={handleExtraFolderObjects}
        onContinue={onContinue}
        isGetFolderListLoading={isGetFolderListLoading}
        isGetExtraFolderListLoading={isGetExtraFolderListLoading}
        cart={cart}
        setCart={setCart}
        onPathValueChange={setPathValue}
      />

      <Modal
        open={uploadModalOpen}
        setOpen={handleModalClose}
        showButtons={false}
        title="Upload Files to Remote Server"
        onBeforeClose={handleBeforeClose}>
        <div className="min-h-100 max-w-250 flex w-full flex-col items-center">
          <div className="mb-4">
            <MessageBanner
              messages={FILE_UPLOAD_DISCLAIMER}
              type={BannerType.Warning}
              size={BannerSize.Small}
            />
          </div>

          <FileUploader
            ref={fileUploaderRef}
            onUpload={handleFileUpload}
            onCancel={cancelUploadFn || undefined}
            onConfirmTerminate={handleConfirmTerminate}
            maxFiles={100}
            isLoading={isLoading}
            progressPercentageList={progressPercentageList}
            prefix={sshUploadPrefix(connectionCredentials)}
            showFolderCreation
            fullDestinationPath={pathValue}
          />
        </div>
      </Modal>
    </>
  );
};

export default SSHFolderBrowser;
